import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { doc, arrayUnion, arrayRemove, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { RadioStation } from '../types/radio';

const LOCAL_STORAGE_KEY = 'aurafy_favorites';
const MAX_WRITES_PER_HOUR = 100;

interface PendingUpdate {
  type: 'add' | 'remove';
  station: RadioStation;
  timestamp: number;
}

interface FavoritesState {
  favorites: RadioStation[];
  pendingUpdates: PendingUpdate[];
  writesThisHour: number;
  lastWriteReset: number;
  userId: string | null;
  setUserId: (userId: string | null) => void;
  addFavorite: (station: RadioStation) => Promise<void>;
  removeFavorite: (station: RadioStation) => Promise<void>;
  setFavorites: (favorites: RadioStation[]) => void;
  syncPendingUpdates: () => Promise<void>;
  isFavorite: (stationId: string) => boolean;
  clearFavorites: () => void;
}

export const useFavoritesStore = create<FavoritesState>()(
  persist(
    (set, get) => ({
      favorites: [],
      pendingUpdates: [],
      writesThisHour: 0,
      lastWriteReset: Date.now(),
      userId: null,

      setUserId: (userId: string | null) => {
        // Only clear favorites if the user ID is changing
        if (get().userId !== userId) {
          set({ userId, favorites: [], pendingUpdates: [] });
        } else {
          set({ userId });
        }
      },

      addFavorite: async (station: RadioStation) => {
        const { favorites, userId, pendingUpdates, writesThisHour, lastWriteReset } = get();

        // Don't add if already in favorites
        if (favorites.some(f => f.stationuuid === station.stationuuid)) {
          return;
        }

        // Update local state immediately
        set({ favorites: [...favorites, station] });

        if (!userId) {
          // Store in pending updates if not logged in
          set({
            pendingUpdates: [...pendingUpdates, { type: 'add', station, timestamp: Date.now() }]
          });
          return;
        }

        // Check if we need to reset write count
        const now = Date.now();
        const hoursSinceLastReset = (now - lastWriteReset) / (60 * 60 * 1000);

        if (hoursSinceLastReset >= 1) {
          set({ writesThisHour: 0, lastWriteReset: now });
        }

        // If we're at the write limit, queue the update
        if (writesThisHour >= MAX_WRITES_PER_HOUR) {
          set({
            pendingUpdates: [...pendingUpdates, { type: 'add', station, timestamp: now }]
          });
          return;
        }

        try {
          const userRef = doc(db, 'users', userId);
          const batch = writeBatch(db);

          // Store the entire station object in Firestore
          batch.update(userRef, {
            'favorites': arrayUnion(station.stationuuid),
            'favorite_stations': arrayUnion(station),
            'total_favorites': favorites.length + 1
          });

          await batch.commit();
          set({ writesThisHour: writesThisHour + 1 });
        } catch (error) {
          console.error('Error adding favorite:', error);
          // Queue for retry
          set({
            pendingUpdates: [...pendingUpdates, { type: 'add', station, timestamp: now }]
          });
        }
      },

      removeFavorite: async (station: RadioStation) => {
        const { favorites, userId, pendingUpdates, writesThisHour, lastWriteReset } = get();

        // Update local state immediately
        set({
          favorites: favorites.filter(f => f.stationuuid !== station.stationuuid)
        });

        if (!userId) {
          // Store in pending updates if not logged in
          set({
            pendingUpdates: [...pendingUpdates, { type: 'remove', station, timestamp: Date.now() }]
          });
          return;
        }

        // Check if we need to reset write count
        const now = Date.now();
        const hoursSinceLastReset = (now - lastWriteReset) / (60 * 60 * 1000);

        if (hoursSinceLastReset >= 1) {
          set({ writesThisHour: 0, lastWriteReset: now });
        }

        // If we're at the write limit, queue the update
        if (writesThisHour >= MAX_WRITES_PER_HOUR) {
          set({
            pendingUpdates: [...pendingUpdates, { type: 'remove', station, timestamp: now }]
          });
          return;
        }

        try {
          const userRef = doc(db, 'users', userId);
          const batch = writeBatch(db);

          batch.update(userRef, {
            'favorites': arrayRemove(station.stationuuid),
            'favorite_stations': arrayRemove(station),
            'total_favorites': Math.max(0, favorites.length - 1)
          });

          await batch.commit();
          set({ writesThisHour: writesThisHour + 1 });
        } catch (error) {
          console.error('Error removing favorite:', error);
          // Queue for retry
          set({
            pendingUpdates: [...pendingUpdates, { type: 'remove', station, timestamp: now }]
          });
        }
      },

      setFavorites: (favorites: RadioStation[]) => {
        // Only update favorites if they're different from current
        const currentFavorites = get().favorites;
        if (JSON.stringify(currentFavorites) !== JSON.stringify(favorites)) {
          set({ favorites });
        }
      },

      isFavorite: (stationId: string) => {
        const { favorites } = get();
        return favorites.some(f => f.stationuuid === stationId);
      },

      clearFavorites: () => {
        const { userId } = get();
        // Only clear if we're not logged in
        if (!userId) {
          set({ favorites: [], pendingUpdates: [] });
        }
      },

      syncPendingUpdates: async () => {
        const { pendingUpdates, userId, writesThisHour, lastWriteReset, favorites } = get();

        if (!userId || pendingUpdates.length === 0) {
          return;
        }

        // Check if we need to reset write count
        const now = Date.now();
        const hoursSinceLastReset = (now - lastWriteReset) / (60 * 60 * 1000);

        if (hoursSinceLastReset >= 1) {
          set({ writesThisHour: 0, lastWriteReset: now });
        }

        // If we're still at the write limit, wait for next sync
        if (writesThisHour >= MAX_WRITES_PER_HOUR) {
          return;
        }

        try {
          const userRef = doc(db, 'users', userId);
          const batch = writeBatch(db);

          // Process all pending updates in order
          pendingUpdates.forEach(update => {
            if (update.type === 'add') {
              batch.update(userRef, {
                'favorites': arrayUnion(update.station.stationuuid),
                'favorite_stations': arrayUnion(update.station),
                'total_favorites': favorites.length
              });
            } else {
              batch.update(userRef, {
                'favorites': arrayRemove(update.station.stationuuid),
                'favorite_stations': arrayRemove(update.station),
                'total_favorites': Math.max(0, favorites.length)
              });
            }
          });

          await batch.commit();
          set({
            pendingUpdates: [],
            writesThisHour: writesThisHour + 1
          });
        } catch (error) {
          console.error('Error syncing pending updates:', error);
          // Keep pending updates for next sync attempt
        }
      }
    }),
    {
      name: LOCAL_STORAGE_KEY,
      partialize: (state) => ({
        favorites: state.favorites,
        pendingUpdates: state.pendingUpdates,
        writesThisHour: state.writesThisHour,
        lastWriteReset: state.lastWriteReset,
        userId: state.userId
      })
    }
  )
); 